import config from "../worldConfig";
import { v4 as uuid } from "uuid";
import avatars from "../avatars.json";
import {
  AgeType,
  Experiments,
  WorldHandler,
  WorldPos,
  WorldState,
} from "./world";
import { createGene, Gene, GeneTrait } from "./genetics";
import {
  DmgStatus,
  isDmgStatus,
  Status,
  StatusHandler as SH,
  StatusHandler,
} from "./status";
import { Dictionary } from "../utils/dictionaries";
import { Possession } from "./ownership";
import { ResourceType } from "./resources";
import { FactionAgent, FactionMembership } from "./social";
const {
  CHARACTER_MAX_HP,
  CHARACTER_AGE_OLD_START,
  CHARACTER_AGE_ADULT_START,
  PREGNANCY_AGE_MAX,
} = config;

export enum StatusType {
  ALIVE = "ALIVE",
  STARVING = "STARVING",
  HAD_SEX = "HAD_SEX",
  PREGNANT = "PREGNANT",
  OLD = "OLD",
  DEAD = "DEAD",
  WOUNDED = "WOUNDED",
}

export type PregnantStatus = Status<StatusType.PREGNANT> & {
  payload: {
    with: string;
    withGenes: Gene[];
  };
};

export enum CharacterSex {
  MALE = "MALE",
  FEMALE = "FEMALE",
  OTHER = "OTHER",
}

export enum AgeTag {
  OLD = "OLD",
  ADULT = "ADULT",
  CHILD = "CHILD",
}

export type Modifier<T> = { trigger: T; value: number };

export const CharacterMaleFirstnames = [
  "Ned",
  "Tyrion",
  "Jame",
  "John",
  "Tywin",
  "Robb",
  "Theon",
  "Jorah",
  "Petyr",
  "Sandor",
  "Davos",
];

export const CharacterFemaleFirstnames = [
  "Cersei",
  "Sansa",
  "Daenerys",
  "Arya",
  "Margaery",
  "Cathlyn",
  "Missandei",
];

export const CharacterLastnames = [
  "Lannister",
  "Stark",
  "Barateon",
  "Greyjoy",
  "Tyrell",
  "Clegane",
  "Snow",
  "Tarly",
  "Bolton",
];

const genesPerFamily: Dictionary<Gene> = {
  Lannister: createGene(GeneTrait.AGILE),
  Stark: createGene(GeneTrait.STRONG),
  Tarly: createGene(GeneTrait.WEAK),
  Tyrell: createGene(GeneTrait.FERTILE),
};

export type CharacterState = FactionAgent & {
  id: string;
  birth: number;
  sex: CharacterSex;
  firstname: string;
  lastname: string;
  statusList: Status<StatusType>[];
  pos: WorldPos;
  hpMax: number;
  parents: {
    father: string;
    mother: string;
  } | null;
  avatar: string;
  genes: Gene[];
  genesLatent: Gene[];
  possessions: Possession<ResourceType>[];
};

export type CharacterOptions = Partial<CharacterState> & { age?: number };

export class CharacterHandler {
  static create = (
    // ONLY FOR BATCH GENERATION => NO COMPUTING LIKE PARENT GENES, etc...
    world: WorldState,
    opt?: CharacterOptions
  ): CharacterState => {
    const sex =
      opt && opt.sex
        ? opt.sex
        : Math.random() < 0.5
        ? CharacterSex.MALE
        : CharacterSex.FEMALE;

    const firstnamePool =
      sex === CharacterSex.MALE
        ? CharacterMaleFirstnames
        : CharacterFemaleFirstnames;

    const lastname =
      opt && opt.lastname
        ? opt.lastname
        : CharacterLastnames[
            Math.floor(Math.random() * CharacterLastnames.length)
          ];

    const genes: Gene[] =
      world.experiments.find((e) => e === Experiments.LASTNAME_TO_GENES) &&
      genesPerFamily[lastname]
        ? [genesPerFamily[lastname]]
        : [];
    const genesLatent: Gene[] = genes; //@TODO GeneTrait should depend on genes
    const possessions: Possession<ResourceType>[] = [
      { type: ResourceType.INFLUENCE, quantity: 0 },
    ];

    const pos =
      opt && opt.pos
        ? opt.pos
        : {
            x: Math.floor(Math.random() * world.size),
            y: Math.floor(Math.random() * world.size),
          };

    const factions: FactionMembership[] =
      opt && opt.factions ? opt.factions : [];

    const baseChar: CharacterState = {
      id: uuid(),
      birth: opt && opt.age ? world.cycles - opt.age : world.cycles,
      sex,
      statusList: [
        { type: StatusType.ALIVE, duration: opt && opt.age ? opt.age : 0 },
      ],
      pos,
      firstname:
        opt && opt.firstname
          ? opt.firstname
          : firstnamePool[Math.floor(Math.random() * firstnamePool.length)],
      lastname,
      hpMax: CHARACTER_MAX_HP,
      parents: opt && opt.parents ? opt.parents : null,
      avatar: avatars[Math.floor(avatars.length * Math.random())],
      genes: opt && opt.genes ? opt.genes : genes,
      genesLatent: opt && opt.genesLatent ? opt.genesLatent : genesLatent,
      possessions: opt && opt.possessions ? opt.possessions : possessions,
      factions,
    };

    return baseChar;
  };

  static doAging = (c: CharacterState, w: WorldState): CharacterState => {
    const age = CharacterHandler.age(c);

    if (age <= CHARACTER_AGE_OLD_START) {
      return c;
    }

    const dmg: number =
      Math.pow(age, 3) / Math.pow(CHARACTER_AGE_OLD_START, 3) - 1 >
      Math.random()
        ? age - CHARACTER_AGE_OLD_START //Math.floor(1 + (age - CHARACTER_AGE_OLD_START) / 12)
        : 0;

    if (dmg === 0) {
      return c;
    }

    return {
      ...c,
      statusList: SH.addDmgStatus(
        SH.createStatus(StatusType.OLD, { dmg }) as DmgStatus<StatusType.OLD>,
        c.statusList
      ),
    };
  };

  static doDying = (c: CharacterState, w: WorldState): CharacterState => {
    if (CharacterHandler.hp(c) > 0) {
      return c;
    }

    const dmgStatus = c.statusList.filter(isDmgStatus);
    const causeStatus = dmgStatus[dmgStatus.length - 1];
    if (causeStatus.type === StatusType.WOUNDED) {
      const killedBy = WorldHandler.getCharacter(
        w,
        causeStatus.payload.by
      ) as CharacterState;
      console.log(
        `${CharacterHandler.fullname(
          c
        )} was killed by ${CharacterHandler.fullname(killedBy)}`
      );
    }

    const deathStatus = StatusHandler.createStatus(StatusType.DEAD, {
      cause: causeStatus,
    });

    return stripStatus(StatusType.ALIVE)(addStatus(deathStatus, c));
  };

  static fullname = (c: CharacterState): string => {
    return `${c.firstname} ${c.lastname}`;
  };

  static hp = (c: CharacterState): number => {
    const dmgStatus: DmgStatus<StatusType>[] = c.statusList.filter(isDmgStatus);

    const dmg = dmgStatus.reduce(
      (totalDmg: number, s: DmgStatus<StatusType>) => totalDmg + s.payload.dmg,
      0
    );

    return CharacterHandler.hpMax(c) - dmg;
  };

  //@TODO: CLEAN THIS UP AFTER GENES PLAYGROUND IS DONE
  static hpMax = (c: CharacterState): number => {
    const modifierList: Modifier<GeneTrait>[] = [
      { trigger: GeneTrait.STRONG, value: 0.2 },
      { trigger: GeneTrait.WEAK, value: -0.2 },
    ];

    const modifier = modifierList
      .map((m) => (c.genes.find((g) => g.trait === m.trigger) ? m.value : 0))
      .reduce((sum, m) => sum + m, 0);

    return c.hpMax * (1 + modifier);
  };

  static age = (
    c: CharacterState,
    ageType: AgeType = AgeType.CYCLE
  ): number => {
    const alive = c.statusList.find((s) => s.type === StatusType.ALIVE);
    if (!alive) {
      return NaN;
    }

    switch (ageType) {
      case AgeType.CYCLE:
        return alive.duration;

      case AgeType.YEAR:
        return Math.floor(alive.duration / 12);

      default:
        throw new Error(`UNKNOWN AGE.TYPE: ${ageType}`);
    }
  };

  static ageTag = (c: CharacterState): AgeTag => {
    const age = CharacterHandler.age(c);
    if (age >= CHARACTER_AGE_OLD_START) {
      return AgeTag.OLD;
    }

    if (age >= CHARACTER_AGE_ADULT_START) {
      return AgeTag.ADULT;
    }

    return AgeTag.CHILD;
  };

  static getOldest = (chars: CharacterState[]): CharacterState | undefined => {
    return chars.reduce(
      (oldest: CharacterState | undefined, c: CharacterState) =>
        oldest !== undefined &&
        CharacterHandler.age(oldest) >= CharacterHandler.age(c)
          ? oldest
          : c,
      undefined
    );
  };

  static canWork = (c: CharacterState): boolean => {
    return (
      CharacterHandler.age(c) >= CHARACTER_AGE_ADULT_START &&
      CharacterHandler.age(c) < CHARACTER_AGE_OLD_START
    ); //@TODO: to update with future status like wounded & so
  };

  static canHaveSex = (c: CharacterState): boolean => {
    return (
      CharacterHandler.age(c) >= CHARACTER_AGE_ADULT_START &&
      (c.sex === CharacterSex.MALE ||
        CharacterHandler.age(c) < PREGNANCY_AGE_MAX)
    ); //@TODO: to update with future status like wounded & so
  };
}

export const stripStatus =
  (status: StatusType, debug?: boolean) =>
  (char: CharacterState): CharacterState => {
    if (debug) {
      console.log(`${char.firstname} ${char.lastname} LOSE STATUS ${status}`);
    }

    return {
      ...char,
      statusList: char.statusList.filter((s) => s.type !== status),
    };
  };

export const addStatus = <T extends StatusType>(
  status: Status<T>,
  char: CharacterState,
  opts?: {
    debug?: boolean;
  }
): CharacterState => {
  if (opts && opts.debug) {
    console.log(
      `${char.firstname} ${char.lastname} EARN STATUS ${status.type}`,
      status
    );
  }

  if (isDmgStatus(status)) {
    return {
      ...char,
      statusList: StatusHandler.addDmgStatus(status, char.statusList),
    };
  }

  return {
    ...char,
    statusList: [...char.statusList, status],
  };
};

export const hasStatus = (
  status: StatusType,
  char: CharacterState
): boolean => {
  return char.statusList.find((s) => s.type === status) != null;
};

export const hasStatusFilter =
  (status: StatusType) =>
  (char: CharacterState): boolean => {
    return char.statusList.find((s) => s.type === status) != null;
  };

export const isAlive = (c: CharacterState) => hasStatus(StatusType.ALIVE, c);
export const isDead = (c: CharacterState) => !hasStatus(StatusType.ALIVE, c);
